import { useState, type FormEvent } from "react";
import { knowledgeBaseViewCopy } from "../app/i18n";
import { knowledgeArticleStatusBadgeClass, knowledgeArticleStatusLabel } from "../app/articleStatus";
import type { AppLanguage } from "../app/language";
import { AnimatedModal } from "../app/ui";
import type { KnowledgeBaseSnapshot, KnowledgeCollectionCreateInput, WorkspaceMember } from "../app/types";

type KnowledgeBaseViewProps = {
  knowledgeBase: KnowledgeBaseSnapshot;
  teamMembers: WorkspaceMember[];
  currentUserEmail: string | null;
  language: AppLanguage;
  isLoadingKnowledgeBase?: boolean;
  canCreateArticle?: boolean;
  canCreateCollection?: boolean;
  onCreateArticle: () => void;
  onCreateCollection: (input: KnowledgeCollectionCreateInput) => Promise<void>;
  onOpenCollection: (collectionId: string) => void;
  onOpenArticle: (articleId: string) => void;
};

const emptyCollectionDraft: KnowledgeCollectionCreateInput = {
  name: "",
  detail: "",
};

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

function EmptyStateCard({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
      <p className="text-sm font-semibold text-[#101828]">{title}</p>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function LoadingStateCard({ label }: { label: string }) {
  return (
    <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
      <LoadingSpinnerBadge label={label} />
    </div>
  );
}

export function KnowledgeBaseView({ knowledgeBase, teamMembers, currentUserEmail, language, isLoadingKnowledgeBase = false, canCreateArticle = true, canCreateCollection = true, onCreateArticle, onCreateCollection, onOpenCollection, onOpenArticle }: KnowledgeBaseViewProps) {
  const copy = knowledgeBaseViewCopy[language];
  const latestArticles = knowledgeBase.articles.filter(article => article.visibility === "workspace").slice(0, 6);
  const privateArticles = knowledgeBase.articles.filter(article => article.visibility === "private");
  const memberNameByEmail = new Map(teamMembers.map(member => [member.email.trim().toLowerCase(), member.name]));
  const normalizedCurrentUserEmail = currentUserEmail?.toLowerCase() ?? null;
  const [isCreateCollectionOpen, setIsCreateCollectionOpen] = useState(false);
  const [collectionDraft, setCollectionDraft] = useState<KnowledgeCollectionCreateInput>(emptyCollectionDraft);
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);

  const handleCreateCollection = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!collectionDraft.name.trim() || !collectionDraft.detail.trim()) {
      setCollectionError(copy.errors.missingFields);
      return;
    }

    setIsSavingCollection(true);
    setCollectionError(null);

    try {
      await onCreateCollection({
        name: collectionDraft.name.trim(),
        detail: collectionDraft.detail.trim(),
      });

      setCollectionDraft(emptyCollectionDraft);
      setIsCreateCollectionOpen(false);
    } catch (error) {
      setCollectionError(error instanceof Error ? error.message : copy.errors.createFailed);
    } finally {
      setIsSavingCollection(false);
    }
  };

  return (
    <>
      <section className="space-y-8 p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">
            {copy.page.description}
          </p>
        </div>

        {canCreateArticle ? (
          <button
            type="button"
            onClick={onCreateArticle}
            className="brand-primary-button rounded-xl px-5 py-3 text-sm font-bold"
          >
            {copy.page.newArticle}
          </button>
        ) : null}
      </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
          <div className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.featured.eyebrow}</p>
                <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.featured.title}</h2>
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              {isLoadingKnowledgeBase ? (
                <div className="lg:col-span-3">
                  <LoadingStateCard label={copy.loading.featured} />
                </div>
              ) : knowledgeBase.featuredArticles.length === 0 ? (
                <div className="lg:col-span-3">
                  <EmptyStateCard title={copy.featured.emptyTitle} detail={copy.featured.emptyDetail} />
                </div>
              ) : knowledgeBase.featuredArticles.map(article => (
                <button key={article.id} type="button" onClick={() => onOpenArticle(article.id)} className="rounded-[24px] bg-[#f9f9fa] p-5 text-left transition-all hover:bg-[#f3f4f6]">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{article.meta}</p>
                  <h3 className="mt-3 font-headline text-xl font-extrabold tracking-[-0.03em] text-[#101828]">{article.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#667085]">{article.summary}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.collections.eyebrow}</p>
                <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.collections.title}</h2>
              </div>
              {canCreateCollection ? (
                <button
                  type="button"
                  onClick={() => {
                    setCollectionError(null);
                    setIsCreateCollectionOpen(true);
                  }}
                  className="brand-primary-button rounded-xl px-5 py-3 text-sm font-bold"
                >
                  {copy.collections.newCollection}
                </button>
              ) : null}
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {isLoadingKnowledgeBase ? (
                <div className="md:col-span-2">
                  <LoadingStateCard label={copy.loading.collections} />
                </div>
              ) : knowledgeBase.collections.length === 0 ? (
                <div className="md:col-span-2">
                  <EmptyStateCard title={copy.collections.emptyTitle} detail={copy.collections.emptyDetail} />
                </div>
              ) : knowledgeBase.collections.map(collection => (
                <button
                  key={collection.id}
                  type="button"
                  onClick={() => onOpenCollection(collection.id)}
                  className="rounded-[24px] border border-[#edf0f2] bg-[#fcfcfd] p-5 text-left transition-all hover:border-[#cfd4dc] hover:bg-white"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-headline text-xl font-extrabold tracking-[-0.03em] text-[#101828]">{collection.name}</h3>
                    <span className="rounded-full bg-[#ecfdf3] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#027a48]">
                      {copy.collections.articleCount(collection.articleCount)}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-[#667085]">{collection.detail}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">
                    {copy.collections.openCollection} <span aria-hidden="true">→</span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.latest.eyebrow}</p>
                <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.latest.title}</h2>
              </div>
              <span className="rounded-full bg-[#f2f4f7] px-4 py-2 text-xs font-semibold text-[#475467]">{copy.latest.visibleCount(latestArticles.length)}</span>
            </div>

            <div className="mt-6 space-y-3">
              {isLoadingKnowledgeBase ? (
                <LoadingStateCard label={copy.loading.latest} />
              ) : latestArticles.length === 0 ? (
                <EmptyStateCard title={copy.latest.emptyTitle} detail={copy.latest.emptyDetail} />
              ) : latestArticles.map(article => (
                <button key={article.id} type="button" onClick={() => onOpenArticle(article.id)} className="w-full rounded-[24px] border border-[#edf0f2] bg-[#fcfcfd] px-5 py-4 text-left transition-all hover:border-[#cfd4dc] hover:bg-white">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{article.meta}</p>
                      <h3 className="mt-2 text-lg font-extrabold tracking-[-0.03em] text-[#101828]">{article.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-[#667085]">{article.summary || copy.latest.noSummary}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${knowledgeArticleStatusBadgeClass(article.status)}`}>{knowledgeArticleStatusLabel(article.status, language)}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>

          <aside className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.popular.eyebrow}</p>
            <div className="mt-5 space-y-3">
              {isLoadingKnowledgeBase ? (
                <LoadingStateCard label={copy.loading.popular} />
              ) : knowledgeBase.popularArticles.length === 0 ? (
                <div className="rounded-2xl bg-[#f9f9fa] px-4 py-4">
                  <p className="text-sm font-semibold text-[#101828]">{copy.popular.emptyTitle}</p>
                  <p className="mt-1 text-xs text-[#667085]">{copy.popular.emptyDetail}</p>
                </div>
              ) : (
                knowledgeBase.popularArticles.map((article, index) => (
                  <button key={article.id} type="button" onClick={() => onOpenArticle(article.id)} className="flex w-full items-start gap-3 rounded-2xl bg-[#f9f9fa] px-4 py-3 text-left transition-all hover:bg-[#f3f4f6]">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-[#006949] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">
                      {index + 1}
                    </div>
                    <p className="text-sm font-semibold text-[#101828]">{article.title}</p>
                  </button>
                ))
              )}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.private.eyebrow}</p>
                <h2 className="mt-2 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.private.title}</h2>
              </div>
              <span className="rounded-full bg-[#f2f4f7] px-4 py-2 text-xs font-semibold text-[#475467]">{copy.private.visibleCount(privateArticles.length)}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-[#667085]">{copy.private.description}</p>

            <div className="mt-5 space-y-3">
              {isLoadingKnowledgeBase ? (
                <LoadingStateCard label={copy.loading.private} />
              ) : privateArticles.length === 0 ? (
                <div className="rounded-2xl bg-[#f9f9fa] px-4 py-4">
                  <p className="text-sm font-semibold text-[#101828]">{copy.private.emptyTitle}</p>
                  <p className="mt-1 text-xs text-[#667085]">{copy.private.emptyDetail}</p>
                </div>
              ) : (
                privateArticles.map(article => {
                  const articleOwnerUserEmail = article.ownerUserEmail ?? null;
                  const articleSharedWithUserEmails = Array.isArray(article.sharedWithUserEmails) ? article.sharedWithUserEmails : [];
                  const isOwner = Boolean(normalizedCurrentUserEmail) && articleOwnerUserEmail === normalizedCurrentUserEmail;
                  const sharedNames = articleSharedWithUserEmails
                    .map(email => memberNameByEmail.get(email) ?? email)
                    .slice(0, 2)
                    .join(", ");

                  return (
                    <button
                      key={article.id}
                      type="button"
                      onClick={() => onOpenArticle(article.id)}
                      className="w-full rounded-2xl border border-[#e7ecf3] bg-[linear-gradient(180deg,#fbfcff_0%,#f8fafc_100%)] dark:bg-[linear-gradient(180deg,#1a1a1a_0%,#23272f_100%)] px-4 py-4 text-left transition-all hover:border-[#cfd4dc] hover:bg-white dark:border-[#23272f] dark:hover:border-[#3a3a3a] dark:hover:bg-[#23272f]"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-[#eef4ff] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#175cd3] dark:bg-[#0f2730] dark:text-[#9fe7ff]">{copy.private.badge}</span>
                            <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${knowledgeArticleStatusBadgeClass(article.status)}`}>{knowledgeArticleStatusLabel(article.status, language)}</span>
                          </div>
                          <p className="mt-3 text-sm font-semibold text-slate-800 dark:text-white">{article.title}</p>
                          <p className="mt-1 text-xs leading-5 text-[#667085]">{article.summary || copy.latest.noSummary}</p>
                        </div>
                      </div>
                      <p className="mt-3 text-xs font-semibold text-[#667085]">
                        {copy.private.owner}: {isOwner ? copy.private.you : (memberNameByEmail.get(articleOwnerUserEmail ?? "") ?? articleOwnerUserEmail ?? copy.private.unknown)}
                        {sharedNames ? copy.private.sharedWithSuffix(sharedNames, articleSharedWithUserEmails.length > 2 ? articleSharedWithUserEmails.length - 2 : 0) : ""}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </section>
          </aside>
        </div>
      </section>

      <AnimatedModal
        open={isCreateCollectionOpen && canCreateCollection}
        onClose={() => {
          setIsCreateCollectionOpen(false);
          setCollectionDraft(emptyCollectionDraft);
          setCollectionError(null);
        }}
        backdropAriaLabel={copy.modal.closeDialog}
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 w-full max-w-2xl rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
            <button
              type="button"
              onClick={() => {
                setIsCreateCollectionOpen(false);
                setCollectionDraft(emptyCollectionDraft);
                setCollectionError(null);
              }}
              aria-label={copy.modal.closeDialog}
              className="absolute right-7 top-7 z-10 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            >
              ×
            </button>

            <div className="pr-16">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.modal.eyebrow}</p>
                <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.modal.title}</h2>
                <p className="mt-2 text-sm text-[#667085]">{copy.modal.description}</p>
              </div>
            </div>

            <form onSubmit={handleCreateCollection} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.name}</span>
                <input
                  type="text"
                  value={collectionDraft.name}
                  onChange={event => setCollectionDraft(current => ({ ...current, name: event.target.value }))}
                  placeholder={copy.modal.namePlaceholder}
                  className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                  required
                />
              </label>

              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.descriptionLabel}</span>
                <textarea
                  value={collectionDraft.detail}
                  onChange={event => setCollectionDraft(current => ({ ...current, detail: event.target.value }))}
                  placeholder={copy.modal.descriptionPlaceholder}
                  className="mt-2 min-h-[120px] w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                  required
                />
              </label>

              {collectionError ? <p className="text-sm font-semibold text-[#b42318]">{collectionError}</p> : null}

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateCollectionOpen(false);
                    setCollectionDraft(emptyCollectionDraft);
                    setCollectionError(null);
                  }}
                  className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
                >
                  {copy.modal.cancel}
                </button>
                <button type="submit" disabled={isSavingCollection} className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-60">
                  {isSavingCollection ? copy.modal.creating : copy.modal.create}
                </button>
              </div>
            </form>
      </AnimatedModal>
    </>
  );
}